import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { inputStyle, buttonStyle } from '../styles';

const GOOGLE_ERROR_MESSAGES: Record<string, string> = {
  google_nie_je_nakonfigurovane: 'Prihlásenie cez Google zatiaľ nie je nastavené.',
  google_zlyhalo: 'Prihlásenie cez Google zlyhalo, skús to znova.',
};

// /prihlasenie — spoločná stránka pre login aj registráciu (toggle) + Google OAuth
// (Blok 4, Týž. 14/14b).
export function AuthPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refresh } = useAuth();
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const googleError = searchParams.get('error');
  const [error, setError] = useState<string | null>(
    googleError ? (GOOGLE_ERROR_MESSAGES[googleError] ?? 'Prihlásenie cez Google zlyhalo.') : null,
  );
  const [loading, setLoading] = useState(false);

  async function submit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await fetch(`/api/v1/auth/${mode === 'login' ? 'login' : 'register'}`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message ?? `${res.status} ${res.statusText}`);
      }
      await refresh();
      navigate('/ucet');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="page page-auth">
      <div className="page-header" style={{ textAlign: 'center' }}>
        <h1>{mode === 'login' ? 'Prihlásenie' : 'Registrácia'}</h1>
        <p>
          {mode === 'login' ? 'Vitaj späť v Corpuls.' : 'Vytvor si účet zadarmo za pár sekúnd.'}
        </p>
      </div>

      <div className="card">
        <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            required
            style={{ ...inputStyle, marginRight: 0, width: '100%' }}
          />
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Heslo (min. 8 znakov)"
            minLength={8}
            required
            style={{ ...inputStyle, marginRight: 0, width: '100%' }}
          />
          {error && (
            <p style={{ color: 'var(--risk-high)', fontSize: '0.85rem', margin: 0 }}>{error}</p>
          )}
          <button
            type="submit"
            disabled={loading}
            style={{ ...buttonStyle, width: '100%', marginTop: '0.4rem' }}
          >
            {loading ? 'Chvíľu...' : mode === 'login' ? 'Prihlásiť sa' : 'Zaregistrovať sa'}
          </button>
        </form>

        <div className="divider-or">alebo</div>

        <a href="/api/v1/auth/google" className="btn-google">
          <GoogleIcon />
          Prihlásiť sa cez Google
        </a>
      </div>

      <div style={{ textAlign: 'center', marginTop: '1.1rem' }}>
        <button
          type="button"
          onClick={() => setMode(mode === 'login' ? 'register' : 'login')}
          className="btn-link"
        >
          {mode === 'login' ? 'Nemáš účet? Zaregistruj sa' : 'Už máš účet? Prihlás sa'}
        </button>
      </div>
    </main>
  );
}

function GoogleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 18 18" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.9c1.7-1.57 2.7-3.88 2.7-6.62Z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.9-2.26c-.8.54-1.84.86-3.06.86-2.35 0-4.34-1.59-5.05-3.72H.96v2.33A9 9 0 0 0 9 18Z"
      />
      <path
        fill="#FBBC05"
        d="M3.95 10.7a5.4 5.4 0 0 1 0-3.4V4.97H.96a9 9 0 0 0 0 8.06l2.99-2.33Z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.46.9 11.43 0 9 0A9 9 0 0 0 .96 4.97L3.95 7.3C4.66 5.17 6.65 3.58 9 3.58Z"
      />
    </svg>
  );
}
